import { useState, useEffect, useMemo } from 'react';
import './FloatingStatus.css';

// Phase timing estimates (seconds) based on optimized pipeline
const PHASE_TIMING = {
  starting: { duration: 2, cumulative: 2 },
  context: { duration: 12, cumulative: 14 },
  searching: { duration: 20, cumulative: 34 },
  deep_research: { duration: 15, cumulative: 49 },
  report: { duration: 45, cumulative: 94 },
} as const;

const TOTAL_ESTIMATED_TIME = 94; // seconds

// Music-themed phrases for variety
const PHASE_PHRASES: Record<string, string[]> = {
  starting: [
    "Warming up the turntables...",
    "Plugging in the amps...",
    "Checking the levels...",
  ],
  context: [
    "Mapping the genre landscape...",
    "Connecting the influences...",
    "Tracing the roots...",
    "Understanding the scene...",
  ],
  searching: [
    "Digging through the crates...",
    "Raiding the record store...",
    "Scouring Bandcamp...",
    "Mining the underground...",
    "Hunting for gems...",
  ],
  deep_research: [
    "Verifying the finds...",
    "Checking the liner notes...",
    "Confirming the connections...",
    "Validating the vanguard...",
  ],
  report: [
    "Crafting your report...",
    "Writing the story...",
    "Connecting the dots...",
    "Putting it all together...",
    "Almost there...",
  ],
};

function getPhasePhrase(phase: string): string {
  const phrases = PHASE_PHRASES[phase] || PHASE_PHRASES.searching;
  return phrases[Math.floor(Math.random() * phrases.length)];
}

interface FloatingStatusProps {
  phase: string;
  message: string;
  isActive: boolean;
  query?: string;
  theme?: 'light' | 'dark';
  startTime?: number; // Unix timestamp when research started
}

export function FloatingStatus({ phase, message, isActive, query, theme = 'light', startTime }: FloatingStatusProps) {
  const [phrase, setPhrase] = useState(() => getPhasePhrase(phase));
  const [elapsed, setElapsed] = useState(0);

  // Update phrase when phase changes
  useEffect(() => {
    setPhrase(getPhasePhrase(phase));
  }, [phase]);

  // Track elapsed time
  useEffect(() => {
    if (!isActive || !startTime) return;

    const updateElapsed = () => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [isActive, startTime]);

  // Rotate phrase every 5 seconds within the same phase
  useEffect(() => {
    if (!isActive) return;
    const interval = setInterval(() => {
      setPhrase(getPhasePhrase(phase));
    }, 5000);
    return () => clearInterval(interval);
  }, [isActive, phase]);

  // Calculate progress percentage and time remaining
  const { progress, timeRemaining } = useMemo(() => {
    const timing = PHASE_TIMING[phase as keyof typeof PHASE_TIMING];
    if (!timing) {
      return { progress: 0, timeRemaining: TOTAL_ESTIMATED_TIME };
    }

    // Base progress from completed phases
    const baseProgress = ((timing.cumulative - timing.duration) / TOTAL_ESTIMATED_TIME) * 100;

    // Add progress within current phase based on elapsed time
    const phaseStart = timing.cumulative - timing.duration;
    const timeInPhase = Math.max(0, elapsed - phaseStart);
    const phaseProgress = Math.min(timeInPhase / timing.duration, 0.95); // Cap at 95% within phase
    const withinPhaseProgress = (phaseProgress * timing.duration / TOTAL_ESTIMATED_TIME) * 100;

    const totalProgress = Math.min(baseProgress + withinPhaseProgress, 98); // Never show 100% until done

    // Estimate remaining time
    const estimatedRemaining = Math.max(0, TOTAL_ESTIMATED_TIME - elapsed);

    return {
      progress: totalProgress,
      timeRemaining: estimatedRemaining,
    };
  }, [phase, elapsed]);

  if (!isActive || phase === 'complete' || phase === 'cached') return null;

  const { mainMessage, subMessage } = getMessages(phase, message, query);

  // Format time remaining
  const formatTime = (seconds: number): string => {
    if (seconds <= 0) return 'almost done';
    if (seconds < 60) return `~${seconds}s left`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `~${mins}m ${secs}s left`;
  };

  return (
    <div className={`floating-footer ${phase}`} data-theme={theme}>
      <div className="footer-content">
        <div className="footer-progress-circle">
          <svg viewBox="0 0 36 36" className="progress-ring">
            <path
              className="progress-ring-bg"
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
            />
            <path
              className="progress-ring-fill"
              strokeDasharray={`${progress}, 100`}
              d="M18 2.0845
                a 15.9155 15.9155 0 0 1 0 31.831
                a 15.9155 15.9155 0 0 1 0 -31.831"
            />
          </svg>
          <span className="progress-percent">{Math.round(progress)}%</span>
        </div>
        <div className="footer-text">
          <span className="footer-main">{mainMessage}</span>
          {subMessage && <span className="footer-sub">{subMessage}</span>}
        </div>
        <div className="footer-time">
          <span className="time-remaining">{formatTime(timeRemaining)}</span>
          <span className="time-phrase">{phrase}</span>
        </div>
      </div>
      <div className="footer-progress-bar">
        <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

function getMessages(phase: string, message: string, query?: string): { mainMessage: string; subMessage?: string } {
  switch (phase) {
    case 'starting':
      return {
        mainMessage: 'Starting research',
        subMessage: query ? `"${query}"` : undefined
      };
    case 'context':
      return {
        mainMessage: 'Understanding the genre',
        subMessage: 'Building context and finding key artists'
      };
    case 'searching':
      return {
        mainMessage: 'Discovering artists',
        subMessage: 'Searching databases and verifying embeds'
      };
    case 'deep_research':
      return {
        mainMessage: 'Enriching data',
        subMessage: 'Getting YouTube and Bandcamp links'
      };
    case 'report':
      return {
        mainMessage: 'Writing your report',
        subMessage: 'Creating sections and narratives'
      };
    default:
      return {
        mainMessage: message || 'Working...',
        subMessage: undefined
      };
  }
}
